
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "src/@/components/ui/dialog";
import { Button } from "src/@/components/ui/button";
import { Textarea } from "src/@/components/ui/textarea";
import { Input } from "src/@/components/ui/input";
import { Badge } from "src/@/components/ui/badge";
import {
    Bot,
    User,
    Send,
    Check,
    X,
    Sparkles,
    ArrowRightLeft,
    Eye,
    RotateCcw,
    ChevronLeft
} from "lucide-react";
import v2_actionPromptImprovement from "src/actions/v2/ai/v2_actionPromptImprovement";
import v3_actionGetAgentFeedbacks from 'src/actions/v3/ai/v3_actionGetAgentFeedbacks';
import { format } from 'date-fns';
import { MessageSquarePlus } from 'lucide-react';

// Aplicar edições cirúrgicas ao prompt
const applyEdits = (prompt, edits, acceptedEdits) => {
    let result = prompt;

    // Aplicar edições na ordem reversa para manter posições corretas
    const sortedEdits = [...edits].map((edit, idx) => ({
        ...edit,
        originalIdx: edit.originalIdx ?? idx
    }));

    for (const edit of sortedEdits) {
        const idx = edit.originalIdx;
        // Pular se não aceita ou se marcada como inválida
        if (!acceptedEdits[idx] || edit.isValid === false) continue;

        if (edit.type === 'replace' || edit.type === 'delete') {
            if (edit.search && result.includes(edit.search)) {
                result = result.replace(edit.search, edit.replace || '');
            }
        } else if (edit.type === 'insert' && edit.anchor) {
            const anchorIdx = result.indexOf(edit.anchor);
            if (anchorIdx !== -1) {
                const insertPos = anchorIdx + edit.anchor.length;
                result = result.slice(0, insertPos) + (edit.replace || '') + result.slice(insertPos);
            }
        }
    }

    return result;
};

// Validar e filtrar edições - remove edições onde o search não existe no prompt
const validateEdits = (prompt, edits) => {
    if (!edits || !Array.isArray(edits)) return [];

    return edits.map((edit, idx) => {
        let isValid = false;

        if (edit.type === 'insert') {
            // Para insert, verifica se o anchor existe
            isValid = edit.anchor && prompt.includes(edit.anchor);
        } else if (edit.type === 'replace' || edit.type === 'delete') {
            // Para replace/delete, verifica se o search existe
            isValid = edit.search && prompt.includes(edit.search);
        }

        return { ...edit, isValid, originalIdx: idx };
    });
};

// Encontrar contexto ao redor do texto de busca
const findEditContext = (prompt, search, contextLines = 2) => {
    if (!search || !prompt) return null;

    const idx = prompt.indexOf(search);
    if (idx === -1) return null;

    const lines = prompt.split('\n');
    let currentPos = 0;
    let startLine = 0;

    for (let i = 0; i < lines.length; i++) {
        if (currentPos + lines[i].length >= idx) {
            startLine = i;
            break;
        }
        currentPos += lines[i].length + 1;
    }

    const contextStart = Math.max(0, startLine - contextLines);
    const searchLines = search.split('\n').length;
    const contextEnd = Math.min(lines.length - 1, startLine + searchLines + contextLines);

    return {
        lineNumber: startLine + 1,
        beforeContext: lines.slice(contextStart, startLine).join('\n'),
        afterContext: lines.slice(startLine + searchLines, contextEnd + 1).join('\n')
    };
};

const DiffViewer = ({ currentPrompt, refinedPrompt, onPromptChange }) => {
    const { t } = useTranslation('settings');
    const diffResult = React.useMemo(() => {
        if (!currentPrompt || !refinedPrompt) return null;
        const rawChanges = diffLines(currentPrompt, refinedPrompt);

        // First pass: Group contiguous changes of same type
        const grouped = [];
        let last = null;
        for (let change of rawChanges) {
            if (last && last.type === change.type) {
                last.value += '\n' + change.value;
            } else {
                last = { ...change };
                grouped.push(last);
            }
        }

        // Second pass: Merge removed + added into 'replacement'
        const merged = [];
        for (let i = 0; i < grouped.length; i++) {
            const current = grouped[i];
            const next = grouped[i + 1];

            if (current.type === 'removed' && next && next.type === 'added') {
                merged.push({
                    type: 'replacement',
                    oldValue: current.value,
                    newValue: next.value
                });
                i++; // Skip next
            } else {
                merged.push(current);
            }
        }

        return merged;
    }, [currentPrompt, refinedPrompt]);

    const [decisions, setDecisions] = useState({});

    // Reset decisions when diffResult changes
    useEffect(() => {
        setDecisions({});
    }, [diffResult]);

    // Calculate final string
    useEffect(() => {
        if (!diffResult) return;

        let buildingStringParts = [];

        diffResult.forEach((part, i) => {
            const status = decisions[i] || 'approved';

            if (part.type === 'same') {
                buildingStringParts.push(part.value);
            } else if (part.type === 'added') {
                if (status === 'approved') buildingStringParts.push(part.value);
            } else if (part.type === 'removed') {
                if (status === 'rejected') buildingStringParts.push(part.value); // Keep original if rejected removal
            } else if (part.type === 'replacement') {
                if (status === 'approved') {
                    buildingStringParts.push(part.newValue);
                } else {
                    buildingStringParts.push(part.oldValue);
                }
            }
        });

        const finalString = buildingStringParts.join('\n');
        onPromptChange(finalString);
    }, [diffResult, decisions, onPromptChange]);

    if (!diffResult) return <div className="p-4 text-xs font-mono text-slate-500">{t('aiAgents.promptModal.noChangesToShow', 'Nenhuma alteração para visualizar.')}</div>;

    return (
        <div className="flex flex-col w-full h-full overflow-y-auto bg-white p-4 font-mono text-xs leading-5">
            {diffResult.map((part, i) => {
                const status = decisions[i] || 'approved';
                const isAdded = part.type === 'added';
                const isRemoved = part.type === 'removed';
                const isSame = part.type === 'same';
                const isReplacement = part.type === 'replacement';

                if (isSame) {
                    return (
                        <div key={i} className="px-3 py-1 flex w-full text-slate-600 border-l-2 border-transparent">
                            <span className="select-none w-6 text-[10px] text-slate-300 text-right mr-3 shrink-0">{i + 1}</span>
                            <span className="whitespace-pre-wrap break-all opacity-80">{part.value}</span>
                        </div>
                    );
                }

                if (isReplacement) {
                    const isApproved = status === 'approved';
                    return (
                        <div key={i} className={`px-3 py-2 flex w-full items-start group transition-all rounded mb-1 bg-amber-50 border-l-2 border-amber-500`}>
                            <div className="flex flex-col items-end mr-3 shrink-0 gap-2">
                                <span className="select-none w-6 text-[10px] text-slate-400 opacity-50 text-right h-5">{i + 1}</span>
                                <div className="flex gap-1 opacity-100 transition-opacity">
                                    <button
                                        onClick={() => setDecisions(prev => ({ ...prev, [i]: 'approved' }))}
                                        className={`p-1 rounded-md border shadow-sm transition-all ${isApproved
                                            ? 'bg-amber-100 text-amber-900 border-amber-200'
                                            : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300 hover:text-slate-600'}`}
                                        title={t('aiAgents.promptModal.acceptChangeTitle', 'Aceitar modificação')}
                                    >
                                        <Check size={12} strokeWidth={3} />
                                    </button>
                                    <button
                                        onClick={() => setDecisions(prev => ({ ...prev, [i]: 'rejected' }))}
                                        className={`p-1 rounded-md border shadow-sm transition-all ${!isApproved
                                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                                            : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300 hover:text-slate-600'}`}
                                        title={t('aiAgents.promptModal.rejectChangeTitle', 'Rejeitar modificação (manter original)')}
                                    >
                                        <X size={12} strokeWidth={3} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 min-w-0 flex flex-col gap-1">
                                <div className={`text-xs ${isApproved ? 'line-through text-red-400 opacity-60' : 'text-blue-800'}`}>
                                    {part.oldValue}
                                </div>
                                <div className={`text-xs ${isApproved ? 'text-emerald-800 font-medium' : 'line-through text-slate-400 opacity-60'}`}>
                                    {part.newValue}
                                </div>
                                <div className="mt-0.5 text-[9px] uppercase font-bold tracking-wider text-amber-600 opacity-70">
                                    {isApproved ? t('aiAgents.promptModal.modifiedLabel', 'Modificado') : t('aiAgents.promptModal.originalKeptLabel', 'Original Mantido')}
                                </div>
                            </div>
                        </div>
                    );
                }

                let containerClass = "px-3 py-2 flex w-full items-start group transition-all rounded mb-1 ";
                let textClass = "whitespace-pre-wrap break-all flex-1 ";

                if (isAdded) {
                    if (status === 'approved') {
                        containerClass += "bg-emerald-50 border-l-2 border-emerald-500";
                        textClass += "text-emerald-800";
                    } else {
                        containerClass += "bg-slate-50 border-l-2 border-slate-300 opacity-60";
                        textClass += "text-slate-500 line-through decoration-slate-400";
                    }
                } else if (isRemoved) {
                    if (status === 'approved') {
                        containerClass += "bg-rose-50 border-l-2 border-rose-500";
                        textClass += "text-rose-800 line-through decoration-rose-400 opacity-80";
                    } else {
                        containerClass += "bg-blue-50 border-l-2 border-blue-500"; // Restored
                        textClass += "text-blue-800";
                    }
                }

                return (
                    <div key={i} className={containerClass}>
                        <div className="flex flex-col items-end mr-3 shrink-0 gap-2">
                            <span className="select-none w-6 text-[10px] text-slate-400 opacity-50 text-right h-5">{i + 1}</span>

                            <div className="flex gap-1 opacity-100 transition-opacity">
                                <button
                                    onClick={() => setDecisions(prev => ({ ...prev, [i]: 'approved' }))}
                                    className={`p-1 rounded-md border shadow-sm transition-all ${status === 'approved'
                                        ? (isAdded ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200')
                                        : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300 hover:text-slate-600'}`}
                                    title={isAdded ? t('aiAgents.promptModal.confirmAdditionTitle', 'Confirmar adição') : t('aiAgents.promptModal.confirmRemovalTitle', 'Confirmar remoção')}
                                >
                                    <Check size={12} strokeWidth={3} />
                                </button>
                                <button
                                    onClick={() => setDecisions(prev => ({ ...prev, [i]: 'rejected' }))}
                                    className={`p-1 rounded-md border shadow-sm transition-all ${status === 'rejected'
                                        ? (isAdded ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-blue-100 text-blue-700 border-blue-200')
                                        : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300 hover:text-slate-600'}`}
                                    title={isAdded ? t('aiAgents.promptModal.rejectAdditionTitle', 'Rejeitar adição') : t('aiAgents.promptModal.keepTextTitle', 'Manter texto (Rejeitar remoção)')}
                                >
                                    <X size={12} strokeWidth={3} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 pt-0.5 min-w-0">
                            <span className={textClass}>{part.value}</span>
                            <div className="mt-1 text-[10px] uppercase font-bold tracking-wider opacity-50">
                                {isAdded
                                    ? (status === 'approved' ? t('aiAgents.promptModal.addedLabel', 'Adicionado') : t('aiAgents.promptModal.ignoredLabel', 'Ignorado'))
                                    : (status === 'approved' ? t('aiAgents.promptModal.removedLabel', 'Removido') : t('aiAgents.promptModal.keptLabel', 'Mantido'))
                                }
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Visualização de diff inline para uma única edição (dark theme com line numbers)
const StepDiffView = ({ currentPrompt, edit }) => {
    const { t } = useTranslation('settings');
    const CONTEXT_LINES = 5;
    const lines = currentPrompt.split('\n');
    const searchText = edit.type === 'insert' ? edit.anchor : edit.search;

    if (!searchText) return <CodeDisplay content={currentPrompt} />;

    const idx = currentPrompt.indexOf(searchText);
    if (idx === -1) return <CodeDisplay content={currentPrompt} />;

    let charCount = 0;
    let startLine = 0;
    for (let i = 0; i < lines.length; i++) {
        if (charCount + lines[i].length >= idx) {
            startLine = i;
            break;
        }
        charCount += lines[i].length + 1;
    }

    const searchLineCount = searchText.split('\n').length;
    const endLine = startLine + searchLineCount;
    const replaceLines = edit.replace ? edit.replace.split('\n') : [];
    const contextStart = Math.max(0, startLine - CONTEXT_LINES);
    const contextEnd = Math.min(lines.length, endLine + CONTEXT_LINES);
    const isInsert = edit.type === 'insert';
    const isReplace = edit.type === 'replace';

    return (
        <div className="flex flex-col min-h-full py-4">
            {contextStart > 0 && (
                <div className="flex px-1 py-1">
                    <span className="w-8 text-right mr-3 text-slate-600 text-[10px]">···</span>
                    <span className="text-slate-600 italic text-[10px]">{t('aiAgents.promptModal.linesAbove', '{{count}} linhas acima', { count: contextStart })}</span>
                </div>
            )}

            {lines.slice(contextStart, startLine).map((line, i) => (
                <div key={`b-${i}`} className="flex px-1 hover:bg-slate-800/30">
                    <span className="select-none w-8 text-slate-600 text-right mr-3 shrink-0 opacity-50 text-[10px] pt-[1px]">{contextStart + i + 1}</span>
                    <span className="text-[#d4d4d4] whitespace-pre-wrap break-all flex-1">{line || ' '}</span>
                </div>
            ))}

            {lines.slice(startLine, endLine).map((line, i) => (
                <div key={`s-${i}`} className={`flex px-1 ${isInsert ? 'bg-slate-800/50' : 'bg-red-900/30'}`}>
                    <span className={`select-none w-8 text-right mr-3 shrink-0 text-[10px] pt-[1px] ${isInsert ? 'text-slate-500' : 'text-red-400 opacity-70'}`}>{startLine + i + 1}</span>
                    <span className={`whitespace-pre-wrap break-all flex-1 ${isInsert ? 'text-[#d4d4d4]' : 'text-red-300 line-through decoration-red-400/50'}`}>{line || ' '}</span>
                </div>
            ))}

            {(isReplace || isInsert) && replaceLines.map((line, i) => (
                <div key={`r-${i}`} className="flex px-1 bg-emerald-900/30">
                    <span className="select-none w-8 text-emerald-400 text-right mr-3 shrink-0 opacity-70 text-[10px] pt-[1px]">+</span>
                    <span className="text-emerald-300 whitespace-pre-wrap break-all flex-1">{line || ' '}</span>
                </div>
            ))}

            {lines.slice(endLine, contextEnd).map((line, i) => (
                <div key={`a-${i}`} className="flex px-1 hover:bg-slate-800/30">
                    <span className="select-none w-8 text-slate-600 text-right mr-3 shrink-0 opacity-50 text-[10px] pt-[1px]">{endLine + i + 1}</span>
                    <span className="text-[#d4d4d4] whitespace-pre-wrap break-all flex-1">{line || ' '}</span>
                </div>
            ))}

            {contextEnd < lines.length && (
                <div className="flex px-1 py-1">
                    <span className="w-8 text-right mr-3 text-slate-600 text-[10px]">···</span>
                    <span className="text-slate-600 italic text-[10px]">{t('aiAgents.promptModal.linesBelow', '{{count}} linhas abaixo', { count: lines.length - contextEnd })}</span>
                </div>
            )}
        </div>
    );
};

// Componente de visualização de edições - Step-by-step (uma alteração por vez)
const EditBasedDiffViewer = ({ currentPrompt, edits, onApply, onCancel }) => {
    const { t } = useTranslation('settings');
    const [currentIdx, setCurrentIdx] = useState(0);
    const [decisions, setDecisions] = useState({});
    const [phase, setPhase] = useState('reviewing');
    const diffScrollRef = useRef(null);

    useEffect(() => {
        if (diffScrollRef.current) {
            diffScrollRef.current.scrollTop = 0;
        }
    }, [currentIdx]);

    if (!edits || edits.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                <Sparkles size={16} className="mr-2 opacity-50" />
                {t('aiAgents.promptModal.noEditsSuggested', 'Nenhuma edição sugerida')}
            </div>
        );
    }

    const currentEdit = edits[currentIdx];
    const totalEdits = edits.length;

    const handleDecision = (accepted) => {
        setDecisions(prev => ({ ...prev, [currentIdx]: accepted }));
        if (currentIdx < totalEdits - 1) {
            setCurrentIdx(prev => prev + 1);
        } else {
            setPhase('summary');
        }
    };

    const handleApply = () => {
        const acceptedMap = {};
        edits.forEach((_, idx) => {
            acceptedMap[idx] = decisions[idx] !== false;
        });
        onApply(acceptedMap);
    };

    const handleBackToReview = () => {
        setCurrentIdx(0);
        setPhase('reviewing');
    };

    // ── Fase summary ──
    if (phase === 'summary') {
        const finalAccepted = edits.filter((_, idx) => decisions[idx] !== false).length;
        const finalRejected = edits.filter((_, idx) => decisions[idx] === false).length;

        return (
            <div className="flex flex-col h-full bg-white">
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-2">
                        {edits.map((edit, idx) => {
                            const isAccepted = decisions[idx] !== false;
                            const context = findEditContext(currentPrompt, edit.search || edit.anchor, 1);

                            return (
                                <div
                                    key={idx}
                                    onClick={() => setDecisions(prev => ({ ...prev, [idx]: !isAccepted }))}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                        isAccepted
                                            ? 'bg-emerald-50/50 border-emerald-200 hover:border-emerald-300'
                                            : 'bg-red-50/50 border-red-200 hover:border-red-300'
                                    }`}
                                >
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                                        isAccepted ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                                    }`}>
                                        {isAccepted ? <Check size={14} /> : <X size={14} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                edit.type === 'replace' ? 'bg-amber-100 text-amber-700' :
                                                edit.type === 'insert' ? 'bg-emerald-100 text-emerald-700' :
                                                'bg-red-100 text-red-700'
                                            }`}>
                                                {edit.type === 'replace' ? t('aiAgents.promptModal.editTypeReplace', 'SUBSTITUIR') : edit.type === 'insert' ? t('aiAgents.promptModal.editTypeInsert', 'INSERIR') : t('aiAgents.promptModal.editTypeRemove', 'REMOVER')}
                                            </span>
                                            {context && (
                                                <span className="text-[10px] text-slate-400 font-mono">{t('aiAgents.promptModal.lineNumber', 'linha {{number}}', { number: context.lineNumber })}</span>
                                            )}
                                        </div>
                                        {edit.description && (
                                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{edit.description}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="border-t bg-slate-50 p-3 flex items-center justify-between gap-3 shrink-0">
                    <div className="text-xs text-slate-500">
                        <span className="text-emerald-600 font-medium">{t('aiAgents.promptModal.acceptedCount', '{{count}} aceita(s)', { count: finalAccepted })}</span>
                        {finalRejected > 0 && (
                            <span className="ml-2 text-red-500 font-medium">{t('aiAgents.promptModal.rejectedCount', '{{count}} rejeitada(s)', { count: finalRejected })}</span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-9 px-4 text-sm"
                            onClick={handleBackToReview}
                        >
                            <ChevronLeft size={16} className="mr-1" />
                            {t('buttons.back', { ns: 'common' })}
                        </Button>
                        <Button
                            size="sm"
                            className="h-9 px-4 text-sm bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={handleApply}
                            disabled={finalAccepted === 0}
                        >
                            <Check size={16} className="mr-1.5" />
                            {t('aiAgents.promptModal.applyButton', 'Aplicar ({{count}})', { count: finalAccepted })}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Fase reviewing ──
    return (
        <div className="flex flex-col h-full bg-[#1e1e1e]">
            <div className="flex-1 overflow-y-auto font-mono text-xs leading-5" ref={diffScrollRef}>
                <StepDiffView currentPrompt={currentPrompt} edit={currentEdit} />
            </div>

            <div className="border-t border-slate-700 bg-[#252526] p-3 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                        currentEdit.type === 'replace' ? 'bg-amber-100 text-amber-700' :
                        currentEdit.type === 'insert' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'
                    }`}>
                        {currentEdit.type === 'replace' ? t('aiAgents.promptModal.editTypeReplace', 'SUBSTITUIR') : currentEdit.type === 'insert' ? t('aiAgents.promptModal.editTypeInsert', 'INSERIR') : t('aiAgents.promptModal.editTypeRemove', 'REMOVER')}
                    </span>
                    {currentEdit.description && (
                        <span className="text-xs text-slate-400 truncate min-w-0">{currentEdit.description}</span>
                    )}
                    <span className="text-[10px] text-slate-500 shrink-0 ml-auto">
                        {t('aiAgents.promptModal.changeCounter', 'Alteração {{current}} de {{total}}', { current: currentIdx + 1, total: totalEdits })}
                    </span>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-9 px-4 text-sm border-red-400/50 text-red-400 hover:bg-red-950/30 hover:text-red-300 bg-transparent"
                        onClick={() => handleDecision(false)}
                    >
                        <X size={16} className="mr-1.5" />
                        {t('aiAgents.promptModal.rejectButton', 'Rejeitar')}
                    </Button>
                    <Button
                        size="sm"
                        className="h-9 px-4 text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleDecision(true)}
                    >
                        <Check size={16} className="mr-1.5" />
                        {t('aiAgents.promptModal.acceptButton', 'Aceitar')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const ChatMessage = ({ id, role, content, suggestedPrompt, onSelect, isSelected }) => {
    const { t } = useTranslation('settings');
    // Verificar se suggestedPrompt é um objeto (novo formato) ou string
    const isStructured = suggestedPrompt && typeof suggestedPrompt === 'object';
    const displayContent = isStructured ? suggestedPrompt.obs : content;

    return (
        <div className={`flex gap-3 mb-4 ${role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`
                flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                ${role === 'user' ? 'bg-indigo-600 text-white' : 'bg-emerald-600 text-white'}
                shadow-md
            `}>
                {role === 'user' ? <User size={16} /> : <Bot size={16} />}
            </div>

            <div className={`flex flex-col max-w-[90%] ${role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                    className={`
                        p-3 rounded-2xl text-sm shadow-sm whitespace-pre-wrap cursor-pointer transition-all
                        ${role === 'user'
                            ? 'bg-indigo-600 text-white rounded-tr-none'
                            : `bg-white border rounded-tl-none ${isSelected ? 'border-indigo-500 ring-1 ring-indigo-500 shadow-md' : 'border-slate-100 text-slate-700 hover:border-indigo-200'}`
                        }
                    `}
                    onClick={() => suggestedPrompt && onSelect(id)}
                >
                    {displayContent}
                </div>

                {suggestedPrompt && role === 'assistant' && (
                    <div
                        className={`flex items-center gap-1 mt-1 text-[10px] cursor-pointer hover:underline ${isSelected ? 'text-indigo-600 font-semibold' : 'text-slate-400'}`}
                        onClick={() => onSelect(id)}
                    >
                        {isSelected ? <Eye size={10} /> : <Sparkles size={10} />}
                        {isSelected ? t('aiAgents.promptModal.viewingChangesLabel', 'Visualizando alterações') : t('aiAgents.promptModal.clickToViewChangesLabel', 'Clique para ver alterações')}
                    </div>
                )}
            </div>
        </div>
    );
};

const ThinkingBubble = () => {
    const { t } = useTranslation('settings');
    return (
        <div className="flex gap-3 mb-4 fade-in-up">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-emerald-600 text-white shadow-md">
                <Bot size={16} />
            </div>
            <div className="flex flex-col items-start">
                <div className="p-3 rounded-2xl rounded-tl-none bg-white border border-slate-100 shadow-sm">
                    <div className="flex space-x-1.5 items-center h-5 px-1">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce"></div>
                    </div>
                </div>
                <span className="text-[10px] text-slate-400 mt-1 ml-1 animate-pulse">{t('aiAgents.promptModal.processingLabel', 'Processando junto a IA...')}</span>
            </div>
        </div>
    );
};

// Recebe `t` do componente chamador (função utilitária pura, sem hook próprio) pois o
// texto gerado é inserido no Textarea do chat e fica visível para o usuário.
const formatFeedbackForPrompt = (feedback, t) => {
    // Filter messages for this chat only
    const targetChatId = feedback.chatObj?.numeroCliente + '@c.us';

    // Sort messages
    const messages = (feedback.mensagens_relacionadas || [])
        .filter(m => m.chatId === targetChatId)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const historyText = messages.map(msg => {
        const role = msg.fromMe ? 'agent' : 'user';
        const isHighlighted = feedback.scope?.messages?.includes(msg.id)
            ? ' ' + t('aiAgents.promptModal.highlightedMessageMarker', '<--- [MENSAGEM DESTACADA PELO USUÁRIO COMO INCORRETA/RELEVANTE]')
            : '';

        let content = msg.body || t('aiAgents.promptModal.noTextFallback', '(sem texto)');
        // If message is not text-based, show its type
        if (msg.type && !['chat', 'text'].includes(msg.type)) {
            content = `[${msg.type.toUpperCase()}]`;
        }

        return `${role}: ${content}${isHighlighted}`;
    }).join('\n');

    const noMessagesFound = t('aiAgents.promptModal.noMessagesFound', 'Nenhuma mensagem encontrada.');
    const wrongMessageLabel = t('aiAgents.promptModal.wrongMessageLabel', 'Mensagem/situação incorreta retornada pelo agente:');
    const correctMessageLabel = t('aiAgents.promptModal.correctMessageLabel', 'Mensagem/sitaução que deveria ser retornada pelo agente:');
    const notInformedFallback = t('aiAgents.promptModal.notInformedFallback', 'Não informado');

    return `----
${t('aiAgents.promptModal.conversationHistoryLabel', 'Historico de conversação:')}
${historyText || noMessagesFound}

${wrongMessageLabel} ${feedback.scope?.msg_wrong || notInformedFallback}

${correctMessageLabel} ${feedback.scope?.msg_correct || notInformedFallback}

---`;
};

const FeedbackSelector = ({ open, onOpenChange, aiAgentId, onSelect }) => {
    const { t } = useTranslation('settings');
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && aiAgentId) {
            setLoading(true);
            v3_actionGetAgentFeedbacks({
                agent_id: aiAgentId,
                page: 1,
                limit: 20,
                sort: 'created_at',
                order: 'desc'
            })
                .then(response => {
                    if (response.status === 200) {
                        setFeedbacks(response.data.data || []);
                    }
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [open, aiAgentId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('aiAgents.promptModal.feedbackSelectorTitle', 'Selecionar Feedback')}</DialogTitle>
                    <DialogDescription>
                        {t('aiAgents.promptModal.feedbackSelectorDescription', 'Escolha um feedback para inserir no contexto de melhoria.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto min-h-0 p-1">
                    {loading ? (
                        <div className="flex justify-center p-8 text-slate-500">
                            <span className="animate-spin mr-2">⏳</span> {t('buttons.loading', { ns: 'common' })}
                        </div>
                    ) : feedbacks.length === 0 ? (
                        <div className="text-center p-8 text-slate-500">
                            {t('aiAgents.promptModal.noFeedbackFound', 'Nenhum feedback encontrado para este agente.')}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {feedbacks.map(fb => (
                                <div
                                    key={fb._id}
                                    className="group p-3 border border-slate-200 rounded-xl hover:border-indigo-400 hover:shadow-sm bg-white transition-all flex items-center justify-between gap-4"
                                >
                                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 line-clamp-2 leading-relaxed">
                                            {fb.scope?.msg_wrong || fb.notes || t('aiAgents.promptModal.noDescriptionFallback', 'Sem descrição')}
                                        </p>

                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                            <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-600">
                                                <User size={10} />
                                                {fb.userObj?.nomeFantasia || t('aiAgents.promptModal.anonymousFallback', 'Anon')}
                                            </span>
                                            <span className="text-[10px] text-slate-400">
                                                {fb.created_at && format(new Date(fb.created_at), 'dd/MM HH:mm')}
                                            </span>
                                        </div>
                                    </div>

                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        className="shrink-0 h-8 px-3 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100"
                                        onClick={() => {
                                            const text = formatFeedbackForPrompt(fb, t);
                                            onSelect(text);
                                            onOpenChange(false);
                                        }}
                                    >
                                        {t('aiAgents.promptModal.useButton', 'Usar')}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

const CodeDisplay = ({ content }) => {
    if (!content) return null;
    const lines = content.split('\n');

    return (
        <div className="w-full h-full bg-[#1e1e1e] flex flex-col overflow-auto font-mono text-xs leading-5">
            <div className="flex flex-col min-h-full py-4">
                {lines.map((line, i) => (
                    <div key={i} className="flex px-1 hover:bg-slate-800/50">
                        <span className="select-none w-8 text-slate-600 text-right mr-3 shrink-0 opacity-50 text-[10px] pt-[1px]">{i + 1}</span>
                        <span className="text-[#d4d4d4] whitespace-pre-wrap break-all flex-1">{line || ' '}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function AdvancedPromptModal({
    open,
    onOpenChange,
    currentPrompt,
    onSave,
    aiAgent
}) {
    const { t } = useTranslation('settings');
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [activeSuggestionId, setActiveSuggestionId] = useState(null);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [finalPromptToApply, setFinalPromptToApply] = useState(null);
    const scrollRef = useRef(null);

    // Initial greeting
    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([
                {
                    id: '1',
                    role: 'assistant',
                    content: t('aiAgents.promptModal.greetingMessage', 'Olá! Sou seu especialista em Engenharia de Prompt. Como posso ajudar a melhorar ou criar seu prompt hoje?'),
                    suggestedPrompt: null
                }
            ]);
        }
    }, [open, messages.length]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const userMsg = {
            id: Date.now().toString(),
            role: 'user',
            content: inputValue
        };

        setMessages(prev => [...prev, userMsg]);
        setInputValue("");
        setIsTyping(true);

        // Call AI API
        try {
            const history = messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            const response = await v2_actionPromptImprovement({
                currentPrompt,
                instruction: inputValue,
                history
            });

            const responseObj = response.data;
            const hasChange = String(responseObj.isChangePrompt).toLowerCase() === 'true';

            // Validar edições - filtrar as que têm search/anchor inválido
            let validatedEdits = [];
            let invalidCount = 0;

            if (hasChange && responseObj.edits?.length > 0) {
                const validated = validateEdits(currentPrompt, responseObj.edits);
                validatedEdits = validated.filter(e => e.isValid);
                invalidCount = validated.filter(e => !e.isValid).length;
            }

            // Preparar resposta com edições validadas
            const finalResponse = {
                ...responseObj,
                edits: validatedEdits
            };

            const hasValidEdits = validatedEdits.length > 0;
            let displayContent = responseObj.obs || t('aiAgents.promptModal.analysisCompleteFallback', 'Análise concluída.');

            // Adicionar aviso se houve edições inválidas filtradas
            if (invalidCount > 0 && hasValidEdits) {
                displayContent += ' ' + t('aiAgents.promptModal.discardedSuggestionsSuffix', '({{count}} sugestão(ões) descartada(s) por não corresponder ao texto do prompt)', { count: invalidCount });
            } else if (invalidCount > 0 && !hasValidEdits) {
                displayContent = t('aiAgents.promptModal.suggestionsNoMatchMessage', 'As sugestões geradas não correspondem ao texto atual do prompt. Tente ser mais específico na sua instrução.');
            }

            const newMsgId = (Date.now() + 1).toString();
            const aiMsg = {
                id: newMsgId,
                role: 'assistant',
                content: displayContent,
                suggestedPrompt: hasValidEdits ? finalResponse : (responseObj.legacyMode ? responseObj : null)
            };

            setMessages(prev => [...prev, aiMsg]);

            if (hasValidEdits || responseObj.legacyMode) {
                setActiveSuggestionId(newMsgId);
            }

        } catch (error) {
            console.error("Error improving prompt:", error);
            const errorMsgId = (Date.now() + 1).toString();
            setMessages(prev => [...prev, {
                id: errorMsgId,
                role: 'assistant',
                content: t('aiAgents.promptModal.processErrorMessage', 'Desculpe, ocorreu um erro ao processar sua solicitação. Tente novamente.'),
                suggestedPrompt: null
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    // Usado apenas para modo legado (DiffViewer)
    const handleApplyChange = () => {
        const activeMsg = messages.find(m => m.id === activeSuggestionId);
        if (!activeMsg || !activeMsg.suggestedPrompt) return;

        const suggestion = activeMsg.suggestedPrompt;

        if (finalPromptToApply) {
            onSave(finalPromptToApply);
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'assistant',
                content: t('aiAgents.promptModal.promptUpdatedMessage', 'Perfeito! O prompt foi atualizado. Mais alguma coisa?'),
                suggestedPrompt: null
            }]);
        } else if (suggestion.promptRefine) {
            onSave(suggestion.promptRefine);
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'assistant',
                content: t('aiAgents.promptModal.promptUpdatedMessage', 'Perfeito! O prompt foi atualizado. Mais alguma coisa?'),
                suggestedPrompt: null
            }]);
        }

        setActiveSuggestionId(null);
        setFinalPromptToApply(null);
    };

    // Derived state for Right Panel
    const activeMsg = messages.find(m => m.id === activeSuggestionId);
    const activeSuggestion = activeMsg ? activeMsg.suggestedPrompt : null;
    const isPreviewing = !!activeSuggestion;

    // Verificar se é o novo formato com edições ou legado
    const hasEditBasedChanges = activeSuggestion?.edits?.length > 0 && !activeSuggestion?.legacyMode;

    const previewPrompt = React.useMemo(() => {
        if (!activeSuggestion) return currentPrompt;
        return typeof activeSuggestion === 'object' ? activeSuggestion.promptRefine : activeSuggestion;
    }, [activeSuggestion, currentPrompt]);

    const handleResetChat = () => {
        setMessages([
            {
                id: '1',
                role: 'assistant',
                content: t('aiAgents.promptModal.greetingMessage', 'Olá! Sou seu especialista em Engenharia de Prompt. Como posso ajudar a melhorar ou criar seu prompt hoje?'),
                suggestedPrompt: null
            }
        ]);
        setActiveSuggestionId(null);
        setFinalPromptToApply(null);
        setInputValue("");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col md:flex-row bg-slate-50">

                {/* Left Panel: Chat Interface */}
                <div className="flex flex-col min-w-0 bg-slate-50/50 w-1/2 border-r">
                    <DialogHeader className="px-5 py-4 border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <div className="absolute inset-0 bg-indigo-500/20 blur-lg rounded-full opacity-70 group-hover:opacity-100 transition-opacity" />
                                    <div className="relative h-11 w-11 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl shadow-sm ring-2 ring-white">
                                        <Sparkles size={20} className="text-white/90" />
                                    </div>
                                    <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 bg-emerald-500 border-[2.5px] border-white rounded-full shadow-sm" title={t('status.online', { ns: 'common' })} />
                                </div>

                                <div className="flex flex-col gap-0.5">
                                    <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
                                        {t('aiAgents.promptModal.title', 'Editor Inteligente')}
                                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-100 text-[10px] h-5 px-2 font-medium">
                                            {t('aiAgents.promptModal.betaBadge', 'BETA')}
                                        </Badge>
                                    </DialogTitle>
                                    <DialogDescription className="text-xs text-slate-500 font-medium">
                                        {t('aiAgents.promptModal.subtitle', 'Converse com a IA para aperfeiçoar seu prompt em tempo real.')}
                                    </DialogDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full"
                                    onClick={handleResetChat}
                                    title={t('aiAgents.promptModal.resetChatTitle', 'Reiniciar conversa')}
                                >
                                    <RotateCcw size={18} />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full"
                                    onClick={() => onOpenChange(false)}
                                >
                                    <X size={20} />
                                </Button>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="flex-1 flex flex-col min-h-0 bg-slate-100/50">
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-4"
                        >
                            {messages.map((msg) => (
                                <ChatMessage
                                    key={msg.id}
                                    {...msg}
                                    onSelect={setActiveSuggestionId}
                                    isSelected={msg.id === activeSuggestionId}
                                />
                            ))}
                            {isTyping && <ThinkingBubble />}
                        </div>

                        {/* Input Area */}
                        <div className="p-3 bg-white border-t">
                            <div className="relative flex gap-2">
                                <Textarea
                                    placeholder={t('aiAgents.promptModal.inputPlaceholder', "Ex: 'Melhore o tom', 'Adicione exemplos'...")}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    rows={5}
                                    className="pr-10 bg-slate-50 focus:bg-white transition-colors text-xs resize-none"
                                />
                                <div className="absolute right-1 bottom-1 flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                                        title={t('aiAgents.promptModal.importFeedbackTitle', 'Importar Feedback do Agente')}
                                        onClick={() => setIsFeedbackModalOpen(true)}
                                    >
                                        <MessageSquarePlus size={14} />
                                    </Button>
                                    <Button
                                        size="icon"
                                        className="h-7 w-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                                        onClick={handleSendMessage}
                                        disabled={!inputValue.trim() || isTyping}
                                    >
                                        <Send size={12} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex flex-col bg-white h-full shadow-xl z-20 min-w-0">
                    {/* Header simplificado - botões só aparecem para modo legado */}
                    <div className="p-3 border-b bg-slate-50 flex justify-between items-center shrink-0 h-[60px]">
                        <div className="flex items-center gap-3">
                            <div className={`p-1.5 rounded ${isPreviewing ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`}>
                                {isPreviewing ? <ArrowRightLeft size={16} /> : <Sparkles size={16} />}
                            </div>
                            <div>
                                <h4 className="font-semibold text-sm text-slate-800">
                                    {isPreviewing
                                        ? (hasEditBasedChanges ? t('aiAgents.promptModal.reviewChangesTitle', 'Revisar Alterações') : t('aiAgents.promptModal.compareChangesTitle', 'Comparação de Alterações'))
                                        : t('aiAgents.promptModal.currentPromptTitle', 'Prompt Atual')}
                                </h4>
                                <p className="text-[10px] text-slate-500">
                                    {isPreviewing
                                        ? (hasEditBasedChanges
                                            ? t('aiAgents.promptModal.reviewEachChangeHint', 'Revise cada alteração individualmente')
                                            : t('aiAgents.promptModal.verifyChangesHint', 'Verifique as mudanças sugeridas antes de aplicar'))
                                        : t('aiAgents.promptModal.currentContentHint', 'Conteúdo atual do seu prompt')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-[10px] hidden md:flex">
                                {t('aiAgents.promptModal.charactersCount', '{{count}} caracteres', { count: currentPrompt?.length || 0 })}
                            </Badge>

                            {/* Botões só para modo legado (DiffViewer) */}
                            {isPreviewing && !hasEditBasedChanges && (
                                <div className="flex gap-2 pl-2 border-l">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                        onClick={() => setActiveSuggestionId(null)}
                                    >
                                        <X size={14} className="mr-1.5" /> {t('buttons.cancel', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                                        onClick={handleApplyChange}
                                    >
                                        <Check size={14} className="mr-1.5" /> {t('buttons.apply', { ns: 'common' })}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-hidden relative">
                        {isPreviewing ? (
                            hasEditBasedChanges ? (
                                <EditBasedDiffViewer
                                    currentPrompt={currentPrompt}
                                    edits={activeSuggestion.edits}
                                    onApply={(acceptedMap) => {
                                        // Aplicar diretamente
                                        const newPrompt = applyEdits(currentPrompt, activeSuggestion.edits, acceptedMap);
                                        const count = Object.values(acceptedMap).filter(Boolean).length;
                                        onSave(newPrompt);
                                        setMessages(prev => [...prev, {
                                            id: Date.now().toString(),
                                            role: 'assistant',
                                            content: t('aiAgents.promptModal.editsAppliedMessage', 'Pronto! {{count}} alteração(ões) aplicada(s) ao prompt. Mais alguma coisa?', { count }),
                                            suggestedPrompt: null
                                        }]);
                                        setActiveSuggestionId(null);
                                    }}
                                    onCancel={() => setActiveSuggestionId(null)}
                                />
                            ) : (
                                <DiffViewer
                                    currentPrompt={currentPrompt}
                                    refinedPrompt={previewPrompt}
                                    onPromptChange={setFinalPromptToApply}
                                />
                            )
                        ) : (
                            <CodeDisplay content={currentPrompt} />
                        )}
                    </div>
                </div>

            </DialogContent >

            <FeedbackSelector
                open={isFeedbackModalOpen}
                onOpenChange={setIsFeedbackModalOpen}
                aiAgentId={aiAgent?._id}
                onSelect={(text) => {
                    setInputValue(prev => (prev ? prev + "\n\n" : "") + text);
                }}
            />
        </Dialog >
    );
}
